<template>
  <section class="px-4 sm:px-6 md:px-10 lg:px-40 mt-10">
    <header class="flex items-end justify-between gap-3 mb-3">
      <div class="min-w-0">
        <h2
          :class="[
            'text-xl sm:text-2xl md:text-3xl font-bold truncate',
            accent === 'red' ? 'text-red-600' : 'text-slate-900',
          ]"
        >
          {{ title }}<span v-if="flair" class="font-light text-base sm:text-lg md:text-xl text-slate-500"> {{ flair }}</span>
        </h2>
        <p v-if="subtitle" class="text-sm text-slate-500 mt-0.5 truncate">{{ subtitle }}</p>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        <span v-if="products.length" class="hidden sm:inline text-xs text-slate-500">{{ $t('product.available', { n: products.length }) }}</span>
        <button
          v-if="products.length > 1"
          type="button"
          class="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
          @click="expanded = !expanded"
        >
          {{ expanded ? $t('rail.collapse') : $t('rail.viewAll') }}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform" :class="expanded ? 'rotate-90' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </header>

    <!-- Skeleton row -->
    <div v-if="loading && !products.length" class="flex gap-3 overflow-hidden">
      <div v-for="i in 6" :key="i" class="shrink-0 basis-[44%] sm:basis-[28%] md:basis-[22%] lg:basis-[18%] aspect-[3/4] rounded-xl bg-slate-100 animate-pulse" />
    </div>

    <!-- Expanded grid: every product visible, multi-row -->
    <div v-else-if="expanded" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
      <div v-for="(p, idx) in products" :key="`grid-${p.id || idx}`">
        <ProductCard
          :product="p"
          :show-wishlist="showWishlist"
          :is-wishlisted="wishlistIds.includes(p.id)"
          :hoverable="true"
          @click="$emit('product-click', p)"
          @wishlist-toggle="$emit('wishlist-toggle', p)"
        />
        <p v-if="countdown && p.ends_in_text" class="mt-1 text-xs font-medium text-red-600">⏱ {{ p.ends_in_text }}</p>
        <p v-else-if="countdown && p.reduction_ends_in" class="mt-1 text-xs font-medium text-red-600">⏱ {{ p.reduction_ends_in }}</p>
      </div>
    </div>

    <!-- Collapsed rail: horizontal snap-scroll w/ arrows -->
    <div v-else class="relative">
      <button
        v-if="canScrollLeft"
        type="button"
        :aria-label="$t('rail.prev')"
        class="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white border border-slate-200 shadow-card items-center justify-center text-slate-700 hover:bg-slate-50"
        @click="scrollBy(-1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
      </button>
      <button
        v-if="canScrollRight"
        type="button"
        :aria-label="$t('rail.next')"
        class="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white border border-slate-200 shadow-card items-center justify-center text-slate-700 hover:bg-slate-50"
        @click="scrollBy(1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
      </button>

      <div
        ref="track"
        class="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1 storefront-rail"
        @scroll.passive="updateArrows"
      >
        <div
          v-for="(p, idx) in products" :key="p.id || idx"
          class="snap-start shrink-0 basis-[44%] sm:basis-[28%] md:basis-[22%] lg:basis-[18%]"
        >
          <ProductCard
            :product="p"
            :show-wishlist="showWishlist"
            :is-wishlisted="wishlistIds.includes(p.id)"
            :hoverable="true"
            @click="$emit('product-click', p)"
            @wishlist-toggle="$emit('wishlist-toggle', p)"
          />
          <p v-if="countdown && p.ends_in_text" class="mt-1 text-xs font-medium text-red-600">⏱ {{ p.ends_in_text }}</p>
          <p v-else-if="countdown && p.reduction_ends_in" class="mt-1 text-xs font-medium text-red-600">⏱ {{ p.reduction_ends_in }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ProductCard from '@/components/products/ProductCard.vue';

export default {
  name: 'ProductRail',
  components: { ProductCard },
  emits: ['product-click', 'wishlist-toggle'],
  props: {
    title:        { type: String, required: true },
    flair:        { type: String, default: '' },
    subtitle:     { type: String, default: '' },
    products:     { type: Array,  default: () => [] },
    loading:      { type: Boolean, default: false },
    wishlistIds:  { type: Array,  default: () => [] },
    showWishlist: { type: Boolean, default: false },
    accent:       { type: String, default: '' },           // '' | 'red'
    countdown:    { type: Boolean, default: false },
    seeAllTo:     { type: [Object, String], default: null },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      resizeHandler: null,
      expanded: false,
    };
  },
  mounted() {
    this.$nextTick(() => this.updateArrows());
    this.resizeHandler = () => this.updateArrows();
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeUnmount() {
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  },
  watch: {
    products() { this.$nextTick(() => this.updateArrows()); },
    loading()  { this.$nextTick(() => this.updateArrows()); },
    expanded() { this.$nextTick(() => this.updateArrows()); },
  },
  methods: {
    updateArrows() {
      const el = this.$refs.track;
      if (!el) { this.canScrollLeft = false; this.canScrollRight = false; return; }
      this.canScrollLeft = el.scrollLeft > 4;
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    },
    scrollBy(direction) {
      const el = this.$refs.track;
      if (!el) return;
      const step = Math.max(240, Math.round(el.clientWidth * 0.8));
      el.scrollBy({ left: direction * step, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.storefront-rail::-webkit-scrollbar { height: 0; }
.storefront-rail { scrollbar-width: none; }
</style>
